import { ReactNode } from 'react'
import { Link, LinkProps } from 'react-router-dom'

import styles from './Button.module.scss'

export type ButtonVariant =
  | 'primary'
  | 'default'
  | 'danger'
  | 'success'
  | 'warning'
  | 'secondary'
  | 'link'
  | 'simple'
  | 'documents'
  | 'download'
  | 'none'

export type ButtonSize = 'xs' | 'sm' | 'md' | 'lg'

export type IconActionVariant = 'edit' | 'delete' | 'archive'

type ButtonStyleProps = {
  variant?: ButtonVariant
  size?: ButtonSize
  /** Material Kit's raised treatment, plus the ripple classes that always accompany it. */
  raised?: boolean
  block?: boolean
  /** Renders a square icon-only action with a hover tooltip built from `title`. */
  iconAction?: IconActionVariant | 'plain'
  /** Dims the control while leaving it clickable, so the click can say why it is unavailable. */
  softDisabled?: boolean
  className?: string
}

const BOOTSTRAP_VARIANTS: Readonly<Record<ButtonVariant, string>> = {
  primary: 'btn-primary',
  default: 'btn-default',
  danger: 'btn-danger',
  success: 'btn-success',
  warning: 'btn-warning',
  secondary: 'btn-secondary',
  link: 'btn-link',
  simple: 'btn-simple',
  documents: '',
  download: '',
  none: '',
}

const MODULE_VARIANTS: Readonly<Partial<Record<ButtonVariant, string>>> = {
  documents: styles.documents,
  download: styles.download,
}

// These stay in `clinical_records.scss` rather than a module: the server-rendered
// `clinical_records/_show_record.html.haml`, which the PDF and the `?pdf=true`
// preview render, emits the same class names.
const ICON_ACTION_VARIANTS: Readonly<Record<IconActionVariant | 'plain', string>> = {
  edit: 'btn-edit-action',
  delete: 'btn-delete-action',
  archive: 'btn-archive-action',
  plain: '',
}

/** The one place in the app allowed to name a Bootstrap `btn*` class. */
function _getButtonClasses({
  variant = 'primary',
  size,
  raised = false,
  block = false,
  iconAction,
  softDisabled = false,
  className = '',
}: ButtonStyleProps): string {
  return [
    'btn',
    BOOTSTRAP_VARIANTS[variant],
    MODULE_VARIANTS[variant] ?? '',
    size ? `btn-${size}` : '',
    raised ? 'btn-raised waves-effect waves-light' : '',
    block ? 'btn-block' : '',
    iconAction ? 'btn-icon-action' : '',
    iconAction ? ICON_ACTION_VARIANTS[iconAction] : '',
    softDisabled ? styles.softDisabled : '',
    className,
  ]
    .filter((part) => part !== '')
    .join(' ')
}

export function Button({
  children,
  type = 'button',
  variant,
  size,
  raised,
  block,
  iconAction,
  softDisabled,
  className,
  ...rest
}: ButtonStyleProps & { children?: ReactNode } & React.ButtonHTMLAttributes<HTMLButtonElement>) {
  return (
    <button type={type} className={_getButtonClasses({ variant, size, raised, block, iconAction, softDisabled, className })} {...rest}>
      {children}
    </button>
  )
}

export function ButtonLink({
  children,
  variant,
  size,
  raised,
  block,
  iconAction,
  softDisabled,
  className,
  ...rest
}: ButtonStyleProps & LinkProps) {
  return (
    <Link className={_getButtonClasses({ variant, size, raised, block, iconAction, softDisabled, className })} {...rest}>
      {children}
    </Link>
  )
}

export function ButtonAnchor({
  children,
  variant,
  size,
  raised,
  block,
  iconAction,
  softDisabled,
  className,
  ...rest
}: ButtonStyleProps & React.AnchorHTMLAttributes<HTMLAnchorElement>) {
  return (
    <a className={_getButtonClasses({ variant, size, raised, block, iconAction, softDisabled, className })} {...rest}>
      {children}
    </a>
  )
}

export function SubmitButton({
  label,
  variant,
  size,
  raised,
  block,
  className,
  ...rest
}: ButtonStyleProps & { label: string } & Omit<React.InputHTMLAttributes<HTMLInputElement>, 'type' | 'value'>) {
  return <input type="submit" value={label} className={_getButtonClasses({ variant, size, raised, block, className })} {...rest} />
}
